export function weightedObjectiveAverage (rows, scoreField, averageMode = 'priority_weighted', allowPartial = false) {
  const hasScore = row => row?.[scoreField] !== null && row?.[scoreField] !== '' && row?.[scoreField] !== undefined
  const objectives = (rows || []).filter(
    row => (row?.objective || '').trim() || (allowPartial && hasScore(row))
  )
  if (objectives.length === 0) return null

  const ratedObjectives = objectives.filter(hasScore)
  if (ratedObjectives.length === 0) return null

  const values = ratedObjectives.map(row => ({
    score: Number(row[scoreField]),
    priority: Number(row.priority)
  }))
  if (values.some(({ score }) => !Number.isFinite(score))) {
    return null
  }

  if (averageMode === 'equal') {
    if (!allowPartial && ratedObjectives.length !== objectives.length) return null
    return (values.reduce((sum, { score }) => sum + score, 0) / values.length).toFixed(2)
  }

  const objectivePriorities = objectives.map(row => Number(row.priority))
  const usesNumericPriorities = objectivePriorities.every(priority => Number.isFinite(priority) && priority > 0)
  if (!usesNumericPriorities) return null

  const totalPriority = objectivePriorities.reduce((sum, priority) => sum + priority, 0)

  return (values.reduce((sum, { score, priority }) => sum + (score * priority), 0) / totalPriority).toFixed(2)
}

export function overallAppraisalAverage (objectiveAverage, competencyAverage) {
  if (objectiveAverage === null || objectiveAverage === undefined || competencyAverage === null || competencyAverage === undefined) {
    return null
  }
  const objectiveScore = Number(objectiveAverage)
  const competencyScore = Number(competencyAverage)
  if (!Number.isFinite(objectiveScore) || !Number.isFinite(competencyScore)) return null
  return ((objectiveScore + competencyScore) / 2).toFixed(2)
}

export function objectiveSuggestedRating (rows, scoreField, objectiveAverage, objectivesConfig = {}) {
  const summaryConfig = objectivesConfig.summary_rating || {}
  if (!summaryConfig.enabled || objectiveAverage === null || objectiveAverage === undefined) return null

  const ratedObjectives = (rows || []).filter(
    row => (
      (row?.objective || '').trim() ||
      (
        objectivesConfig.allow_partial_average &&
        row?.[scoreField] !== null &&
        row?.[scoreField] !== '' &&
        row?.[scoreField] !== undefined
      )
    )
  )
  const niOverrideValue = Number(summaryConfig.ni_override_value)
  if (
    Number.isFinite(niOverrideValue) &&
    ratedObjectives.some(row => Number(row[scoreField]) === niOverrideValue)
  ) {
    return summaryConfig.ni_override_label || 'Needs Improvement'
  }

  const average = Number(objectiveAverage)
  if (!Number.isFinite(average)) return null
  const range = (summaryConfig.ranges || [])
    .map(item => ({ ...item, max: Number(item?.max) }))
    .filter(item => Number.isFinite(item.max) && item.label)
    .sort((a, b) => a.max - b.max)
    .find(item => average <= item.max)
  return range?.label || null
}

function summaryRatingName (value, formConfig = {}) {
  if (value === null || value === undefined || value === '') return null
  const average = Number(value)
  if (!Number.isFinite(average)) return null

  const configuredRanges = formConfig?.objectives?.summary_rating?.ranges
  const ranges = Array.isArray(configuredRanges)
    ? configuredRanges
    : [
        { max: 1.5, label: 'Needs Improvement' },
        { max: 1.75, label: 'Meets Expectations-' },
        { max: 2.25, label: 'Meets Expectations' },
        { max: 2.5, label: 'Meets Expectations+' },
        { max: 2.75, label: 'Exceeds Expectations-' },
        { max: 3.25, label: 'Exceeds Expectations' },
        { max: 3.5, label: 'Exceeds Expectations+' },
        { max: 4, label: 'Excels' }
      ]

  const range = ranges
    .map(item => ({ ...item, max: Number(item?.max) }))
    .filter(item => Number.isFinite(item.max) && item.label)
    .sort((a, b) => a.max - b.max)
    .find(item => average <= item.max)
  return range?.label || ranges.at(-1)?.label || null
}

export function competencySuggestedRating (ratings, competencyAverage, formConfig = {}) {
  if (competencyAverage === null || competencyAverage === undefined) return null

  const usesNiOverride = formConfig?.competencies?.category_average_display === 'rating_name'
  const niValues = new Set(
    (formConfig?.rating_options || [])
      .filter(option => option?.label === 'NI')
      .map(option => Number(option.value))
      .filter(Number.isFinite)
  )
  if (usesNiOverride && (ratings || []).some(rating => niValues.has(Number(rating)))) {
    return formConfig?.objectives?.summary_rating?.ni_override_label || 'Needs Improvement'
  }

  return summaryRatingName(competencyAverage, formConfig)
}

export function overallSuggestedRating (
  objectiveRating,
  competencyRating,
  overallAverage,
  formConfig = {}
) {
  const niLabel = formConfig?.objectives?.summary_rating?.ni_override_label || 'Needs Improvement'
  if (objectiveRating === niLabel || competencyRating === niLabel) return niLabel
  return summaryRatingName(overallAverage, formConfig)
}
